import { dealRoomBaseSections, dealRoomDisabledSections, dealRoomExtraSections } from '@app/constants/dealRoom';
import { DealRoomSection } from '@app/constants/enums/deal-room';
import { IComparison } from '@app/api/comparison.api';
import {
  IDealRoomComparisonSection,
  IDealRoomDocument,
  IDealRoomMenuSection,
  IDealRoomOfferSection,
  IDealRoomSection,
  IDealRoomSectionContent,
  IDealRoomSectionContentWithKey,
} from '@app/interfaces/deal-room';
import { IDealRoomCategory } from '@app/interfaces/deal-room-category';
import { IDealRoomMenu } from '@app/interfaces/deal-room-menu';
import { isObject } from 'lodash';

const COMPARISON_LABEL_KEY = 'dealRooms.sections.comparison';
const COMPARISON_TITLE_KEY = 'dealRooms.sections.comparisonTitle';

/** The `t` the menu helpers accept — the same shape `useTranslation` returns, interpolation included. */
type Translate = (key: string, options?: Record<string, unknown>) => string;

/**
 * A comparison's own name, e.g. `מנורה מול איילון`. Falls back to the shared
 * label when the record is missing an insurer, so an entry is never nameless.
 */
const getComparisonLabel = (comparison: IComparison, t?: Translate): string | null => {
  if (!t) return null;
  const { barozOfferCompany, competitorOfferCompany } = comparison;
  return barozOfferCompany && competitorOfferCompany
    ? t(COMPARISON_TITLE_KEY, { baroz: barozOfferCompany, competitor: competitorOfferCompany })
    : t(COMPARISON_LABEL_KEY);
};

/**
 * A stored setting always wins, so hiding a section sticks. Only the comparisons
 * parent has a fallback, and only while nothing is stored for it: rooms predating
 * the parent have no entry to read, and without this a comparison an admin had
 * already turned on would sit behind a parent nobody thought to turn on.
 */
const isSectionDisplayed = (item: IDealRoomSectionContent, section?: { display?: boolean } | null): boolean => {
  if (section) return section.display ?? false;
  if (item.key === DealRoomSection.COMPARISONS) return (item.subMenu || []).some((sub) => sub.display);
  return false;
};

export const getMappedSections = (s: IDealRoomSectionContent | IDealRoomSectionContentWithKey): IDealRoomSection => {
  const category = s?.category && isObject(s.category) ? s?.category?.id : s?.category || null;
  return {
    section: s.key as DealRoomSection,
    category,
    label: s.label,
    display: s.display,
    default: s.default,
    highlighted: s.highlighted,
    isAdditional: s.isAdditional,
  };
};

export const getMappedOfferSections = (s: IDealRoomSectionContent | IDealRoomDocument): IDealRoomOfferSection => {
  const category = s?.category && isObject(s.category) ? s?.category?.id : s?.category || null;
  return {
    file: s.file || '',
    category,
    defaultLabel: s.labelText || '',
    label: s.label,
    display: s.display,
    default: s.default,
    highlighted: s.highlighted,
  };
};

export const getMappedAdvancedOfferSections = (s: IDealRoomDocument): IDealRoomOfferSection => {
  return {
    file: s.file || '',
    defaultLabel: s.labelText || '',
    order: s.order,
    description: s.description,
    label: s.label,
    display: s.display,
    default: s.default,
    highlighted: s.highlighted,
  };
};

export const getMappedComparisonSections = (s: IDealRoomSectionContent): IDealRoomComparisonSection => {
  const category = s?.category && isObject(s.category) ? s?.category?.id : s?.category || null;
  return {
    comparison: s.comparison?.id || '',
    category,
    label: s.label,
    display: s.display,
    default: s.default,
    highlighted: s.highlighted,
  };
};

export const getMappedMenuSections = (s: IDealRoomSectionContent): IDealRoomMenuSection => {
  const category = s?.category && isObject(s.category) ? s?.category?.id : s?.category || null;
  return {
    menu: s.dealRoomMenu?.id || '',
    category,
    label: s.label,
    display: s.display,
    default: s.default,
    highlighted: s.highlighted,
  };
};

export const getFilteredOfferSectionSections = (sections: IDealRoomDocument[] | []): IDealRoomDocument[] => {
  return (sections as IDealRoomDocument[]).sort((a, b) => a.order - b.order);
};

const getSectionByKey = (
  section: IDealRoomSectionContent,
  sections: IDealRoomSection[],
  offerSections: IDealRoomOfferSection[],
  menuSections: IDealRoomMenuSection[],
  comparisonSections: IDealRoomComparisonSection[] = [],
): IDealRoomSection | IDealRoomOfferSection | IDealRoomMenuSection | IDealRoomComparisonSection | undefined => {
  if (section.isExternal) {
    return (offerSections || []).find((s) => s.defaultLabel === section.labelText);
  }
  if (section.dealRoomMenu) {
    return (menuSections || []).find((s) => (isObject(s.menu) ? s.menu.id : s.menu) === section.key);
  }
  if (section.comparison) {
    return (comparisonSections || []).find(
      (s) => (isObject(s.comparison) ? s.comparison.id : s.comparison) === section.key,
    );
  }
  return sections.find(
    (s) => s.section === section.key || (s.category && (s.category as IDealRoomCategory)?.id === section.key),
  );
};

export const getAllSections = (
  sections: IDealRoomSection[],
  offerSections: IDealRoomOfferSection[],
  menuSections: IDealRoomMenuSection[],
  comparisonSections: IDealRoomComparisonSection[] = [],
  t?: Translate,
): IDealRoomSectionContent[] => {
  const menuMappedSections = (menuSections || []).map((s, i) => {
    const category = s?.category && isObject(s.category) ? s?.category?.id : s?.category || null;
    return {
      key: (s.menu as IDealRoomMenu).id || `menu-${i + 1}`,
      category,
      labelKey: (s.menu as IDealRoomMenu).name || '',
      labelText: (s.menu as IDealRoomMenu).id || '',
      display: s.display ?? false,
      highlighted: s.highlighted ?? false,
      default: s.default ?? false,
      label: s.label || null,
      isEditing: false,
      isAdditional: false,
      dealRoomMenu: isObject(s.menu) ? (s.menu as IDealRoomMenu) : null,
    };
  });

  const comparisonEntries = (comparisonSections || []).filter((s) => isObject(s.comparison));
  const comparisonMappedSections = comparisonEntries.map((s) => {
    const comparison = s.comparison as IComparison;
    const category = s?.category && isObject(s.category) ? s?.category?.id : s?.category || null;
    return {
      key: comparison.id,
      category,
      labelKey: COMPARISON_LABEL_KEY,
      labelText: COMPARISON_LABEL_KEY,
      display: s.display ?? false,
      highlighted: s.highlighted ?? false,
      default: s.default ?? false,
      // The payload carries no per-comparison label, so an untouched entry is
      // named after its two insurers; admins can still rename it in the menu.
      label: s.label || getComparisonLabel(comparison, t),
      isEditing: false,
      isAdditional: false,
      comparison,
    };
  });

  const allSections = dealRoomBaseSections
    .concat(
      dealRoomDisabledSections
        .filter((item) => sections?.some((s) => s.section === item.key))
        .map((s) => {
          const sec = sections.find((sec) => sec.section === s.key);
          const category = sec?.category && isObject(sec.category) ? sec?.category?.id : sec?.category || null;
          return {
            ...s,
            category,
            display: sec?.display ?? false,
            highlighted: sec?.highlighted ?? false,
            default: sec?.default ?? false,
            label: sec?.label || null,
            isAdditional: sec?.isAdditional ?? false,
          };
        }),
    )
    .concat(menuMappedSections as IDealRoomSectionContent[])
    .concat(dealRoomExtraSections as IDealRoomSectionContent[]);

  /**
   * Comparisons hang off their own parent exactly as documents hang off
   * DOCUMENTS. With none to show the parent would be an empty heading, so it is
   * dropped rather than rendered bare.
   */
  const comparisonsIndex = allSections.findIndex((sec) => sec.key === DealRoomSection.COMPARISONS);
  if (comparisonsIndex !== -1) {
    if (comparisonMappedSections.length > 0) {
      allSections[comparisonsIndex] = {
        ...allSections[comparisonsIndex],
        // A room predating the parent has nothing stored for it, and `getSectionByKey`
        // then leaves this value untouched — so the children decide it here as well.
        display: comparisonMappedSections.some((sub) => sub.display),
        subMenu: comparisonMappedSections as IDealRoomSectionContent[],
      };
    } else {
      allSections.splice(comparisonsIndex, 1);
    }
  }

  const offerLength = (offerSections || []).length;
  const externalSections = (offerSections || [])
    .sort((a, b) => (a.order || offerLength) - (b.order || offerLength))
    .map((s, i) => ({
      key: `external-${i + 1}`,
      labelKey: s.defaultLabel,
      labelText: s.defaultLabel,
      description: s.description || '',
      display: s.display ?? false,
      highlighted: s.highlighted ?? false,
      default: s.default ?? false,
      label: s.label || null,
      file: s.file,
      isEditing: false,
      isAdditional: false,
      isExternal: true,
    }));
  const documentIndex = allSections.findIndex((sec) => sec.key === DealRoomSection.DOCUMENTS);
  if (documentIndex !== -1 && externalSections.length > 0) {
    allSections[documentIndex].subMenu = externalSections;
  }

  return allSections;
};

export const getMenuSections = (
  sections: IDealRoomSection[],
  offerSections: IDealRoomOfferSection[],
  menuSections: IDealRoomMenuSection[],
  isAdmin: boolean,
  comparisonSections: IDealRoomComparisonSection[] = [],
  t?: Translate,
): IDealRoomSectionContent[] => {
  const allSections = getAllSections(sections, offerSections, menuSections, comparisonSections, t);

  const activeSections = allSections
    .filter((item) => {
      if (isAdmin) return true;
      const sec = getSectionByKey(item, sections, offerSections, menuSections, comparisonSections);
      return isSectionDisplayed(item, sec);
    })
    .map((item) => {
      const section = getSectionByKey(item, sections, offerSections, menuSections, comparisonSections);
      if (!section) {
        return item;
      }

      if (!('subMenu' in section) || !section.subMenu || section.subMenu.length === 0) {
        return {
          ...item,
          label: section.label || item.label || null,
          highlighted: section.highlighted ?? false,
          display: isSectionDisplayed(item, section),
          default: section.default ?? false,
          subMenu: isAdmin ? item.subMenu : item.subMenu?.filter((sub) => sub.display),
        };
      }

      return {
        ...item,
        label: section.label || item.label || null,
        highlighted: section.highlighted ?? false,
        display: isSectionDisplayed(item, section),
        default: section.default ?? false,
        subMenu: item.subMenu
          ?.filter((sub) => {
            if (isAdmin) return true;
            const subMenu: IDealRoomSection[] = Array.isArray((section as IDealRoomSection)?.subMenu)
              ? ((section as IDealRoomSection).subMenu as IDealRoomSection[])
              : [];
            const subSec = getSectionByKey(sub, subMenu, offerSections, menuSections, comparisonSections);
            return subSec?.display;
          })
          .map((sub) => {
            const subMenu: IDealRoomSection[] = Array.isArray((section as IDealRoomSection)?.subMenu)
              ? ((section as IDealRoomSection).subMenu as IDealRoomSection[])
              : [];
            const subSec = getSectionByKey(sub, subMenu, offerSections, menuSections, comparisonSections);
            if (!subSec) {
              return sub;
            }
            return {
              ...sub,
              label: subSec.label || null,
              highlighted: subSec.highlighted ?? false,
              display: subSec.display ?? false,
              default: subSec.default ?? false,
            };
          }),
      };
    });
  return activeSections;
};

export const getDisplayMenuSections = (
  sections: IDealRoomSection[],
  offerSections: IDealRoomOfferSection[],
  menuSections: IDealRoomMenuSection[],
  isAdmin: boolean,
  comparisonSections: IDealRoomComparisonSection[] = [],
  t?: Translate,
): IDealRoomSectionContent[] => {
  const allSections = getAllSections(sections, offerSections, menuSections, comparisonSections, t);

  const categoryMenus = sections.filter((s) => s.category && !s.section) as IDealRoomSection[];

  const menuSectionsWithSubMenu = allSections.reduce((acc: IDealRoomSectionContent[], item) => {
    if (item.category) {
      const existingMenu = acc.find((i) => i.category && i.category === item.category);
      if (existingMenu) {
        existingMenu.subMenu = existingMenu.subMenu ? [...existingMenu.subMenu, item] : [item];
      } else {
        const categoryMenu = categoryMenus.find((c) => (c.category as IDealRoomCategory)?.id === item.category);
        if (categoryMenu) {
          const categoryName = (categoryMenu.category as IDealRoomCategory)?.name || '';
          const categoryId = (categoryMenu.category as IDealRoomCategory)?.id || '';
          acc.push({
            key: categoryId,
            category: categoryId,
            isCategory: true,
            labelKey: categoryName,
            labelText: categoryName,
            display: categoryMenu.display ?? false,
            highlighted: categoryMenu.highlighted ?? false,
            default: categoryMenu.default ?? false,
            isEditing: false,
            isAdditional: categoryMenu.isAdditional ?? false,
            subMenu: [item],
            label: categoryMenu.label || categoryName,
          });
        }
      }
    } else {
      acc.push(item);
    }
    return acc;
  }, []);

  const activeSections = menuSectionsWithSubMenu
    .filter((item) => {
      if (isAdmin) return true;
      if (item.isCategory) {
        return item?.display;
      }
      const sec = getSectionByKey(item, sections, offerSections, menuSections, comparisonSections);
      return isSectionDisplayed(item, sec);
    })
    .map((item) => {
      const section = item.isCategory
        ? item
        : getSectionByKey(item, sections, offerSections, menuSections, comparisonSections);
      if (!section) {
        return item;
      }

      if (!('subMenu' in section) || !section.subMenu || section.subMenu.length === 0) {
        return {
          ...item,
          label: section.label || item.label || null,
          highlighted: section.highlighted ?? false,
          display: isSectionDisplayed(item, section),
          default: section.default ?? false,
          subMenu: isAdmin ? item.subMenu : item.subMenu?.filter((sub) => sub.display),
        };
      }

      return {
        ...item,
        label: section.label || item.label || null,
        highlighted: section.highlighted ?? false,
        display: isSectionDisplayed(item, section),
        default: section.default ?? false,
        subMenu: item.subMenu
          ?.filter((sub) => {
            if (isAdmin) return true;
            if (item.isCategory) {
              return sub?.display;
            }
            const subMenu: IDealRoomSection[] = Array.isArray((section as IDealRoomSection)?.subMenu)
              ? ((section as IDealRoomSection).subMenu as IDealRoomSection[])
              : [];
            const subSec = getSectionByKey(sub, subMenu, offerSections, menuSections, comparisonSections);
            return subSec?.display;
          })
          .map((sub) => {
            const subMenu: IDealRoomSection[] = Array.isArray((section as IDealRoomSection)?.subMenu)
              ? ((section as IDealRoomSection).subMenu as IDealRoomSection[])
              : [];
            const subSec = item.isCategory
              ? sub
              : getSectionByKey(sub, subMenu, offerSections, menuSections, comparisonSections);
            if (!subSec) {
              return sub;
            }
            return {
              ...sub,
              label: subSec.label || null,
              highlighted: subSec.highlighted ?? false,
              display: subSec.display ?? false,
              default: subSec.default ?? false,
            };
          }),
      };
    });
  return activeSections;
};

export const getSaveMenuSections = (
  sections: IDealRoomSectionContent[],
  dealRoomSections: IDealRoomSection[],
  newCategories: string[],
): IDealRoomSectionContentWithKey[] => {
  const categorySections: IDealRoomSectionContentWithKey[] = sections
    .filter((s) => s.category)
    .map((s) => {
      const categoryId = s?.category && isObject(s.category) ? s?.category?.id : s?.category;
      const section =
        sections.find((sec) => {
          return sec.key === categoryId && sec.isCategory;
        }) ||
        dealRoomSections.find(
          (sec) =>
            !sec.section &&
            sec.category &&
            ((sec.category as IDealRoomCategory)?.id === categoryId || sec.category === categoryId),
        );
      return {
        labelKey: s.labelKey,
        labelText: s.labelText,
        key: null,
        category: categoryId,
        display: categoryId && newCategories.includes(categoryId as string) ? true : section?.display || false,
        highlighted: section?.highlighted || false,
        default: section?.default || false,
        label: section?.label || null,
        isEditing: false,
        isAdditional: true,
      };
    })
    .filter((s) => s.category);

  const uniqueCategorySections = categorySections.reduce((acc: IDealRoomSectionContentWithKey[], item) => {
    const existingCategory = acc.find((i) => i.category === item.category);
    if (!existingCategory) {
      acc.push(item);
    }
    return acc;
  }, []);

  return (sections as unknown as IDealRoomSectionContentWithKey[])
    .filter((s) => !s.isCategory)
    .concat(uniqueCategorySections);
};
